import React, { useState, useMemo } from 'react';
import SchoolServiceAdmin from '../../services/SchoolServiceAdmin';
import { levelColors, levelTranslations } from '../ConstFuncs';

const ArchivedStudentList = ({
  allArchivedStudents,
  loading,
  fetchArchivedStudents,
  isSuperAdmin,
}) => {
  const [isStudentsTableVisible, setIsStudentsTableVisible] =
    useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });

  const toggleStudentsTableVisibility = () => {
    setIsStudentsTableVisible(!isStudentsTableVisible);
  };

  const handleDelete = async (studentId) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this archived student?',
    );
    if (!confirmDelete) return;

    setDeleting(true);
    try {
      await SchoolServiceAdmin.deleteArchivedStudent(studentId); // API call to delete student
      alert('Student deleted successfully.');
      fetchArchivedStudents(); // Re-fetch the list of archived students after deletion
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Failed to delete the student. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Sorting logic for archived students
  const sortedArchivedStudents = useMemo(() => {
    if (!sortConfig.key) return allArchivedStudents;

    return [...allArchivedStudents].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      // Handle arrays like classDaysOfWeek
      if (Array.isArray(valA)) valA = valA.join(', ');
      if (Array.isArray(valB)) valB = valB.join(', ');

      const isNullA =
        valA === null || valA === undefined || valA === '';
      const isNullB =
        valB === null || valB === undefined || valB === '';

      // Handle null/undefined/empty string
      if (isNullA && isNullB) return 0;
      if (isNullA) return sortConfig.direction === 'asc' ? 1 : -1;
      if (isNullB) return sortConfig.direction === 'asc' ? -1 : 1;

      // Compare
      if (typeof valA === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      if (typeof valA === 'number') {
        return sortConfig.direction === 'asc'
          ? valA - valB
          : valB - valA;
      }

      // fallback
      return 0;
    });
  }, [allArchivedStudents, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <>
      <div className="inline-flex items-center justify-center w-full ">
        <hr className="w-96 h-1 my-8 bg-cyan-800 border-0 rounded " />
        <div className="absolute px-4 -translate-x-1/2 bg-white left-1/2 space-x-2">
          <button
            onClick={toggleStudentsTableVisibility}
            className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
          >
            {isStudentsTableVisible
              ? 'Hide All Archived Students'
              : 'Show All Archived Students'}
          </button>
        </div>
      </div>

      {!loading && isStudentsTableVisible && (
        <div className="my-4 max-h-[600px] overflow-y-auto border rounded">
          {' '}
          {/* Added max-w-screen-xl */}{' '}
          {/* This container makes the table scrollable */}
          <table className="min-w-full">
            <thead className="bg-gray-800 text-white sticky top-0">
              <tr>
                <th className="py-2 px-4">#</th>

                {[
                  { key: 'id', label: 'ID' },
                  { key: 'firstName', label: 'First Name' },
                  { key: 'lastName', label: 'Last Name' },
                  {
                    key: 'firstNameArabic',
                    label: 'First Name Arabic',
                  },
                  {
                    key: 'lastNameArabic',
                    label: 'Last Name Arabic',
                  },
                  { key: 'email', label: 'Parents Email' },

                  { key: 'level', label: 'Student Level' },
                ].map((col) => (
                  <th
                    key={col.key}
                    className="py-2 cursor-pointer select-none"
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortConfig.key === col.key &&
                      (sortConfig.direction === 'asc'
                        ? ' 🔼'
                        : ' 🔽')}
                  </th>
                ))}
                {isSuperAdmin && <th className="py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedArchivedStudents?.map((myStudent, index) => (
                <tr
                  className="border-b text-center"
                  key={myStudent.id}
                >
                  {/* Row Number */}
                  <td className="py-2 px-4 font-medium text-gray-500">
                    {index + 1}
                  </td>
                  <td className="py-2 px-4">{myStudent.id}</td>
                  <td className="py-2">{myStudent.firstName}</td>
                  <td className="py-2">{myStudent.lastName}</td>
                  <td className="py-2">
                    {myStudent.firstNameArabic}
                  </td>
                  <td className="py-2">{myStudent.lastNameArabic}</td>
                  <td className="py-2">{myStudent.email}</td>

                  <td
                    className={`py-2 font-semibold text-gray-300 ${
                      myStudent.level != null
                        ? levelColors[myStudent.level]
                        : 'bg-slate-600'
                    }`}
                  >
                    {myStudent.level != null
                      ? levelTranslations[myStudent.level]
                      : 'لم يتم الاختيار بعد'}
                  </td>
                  {isSuperAdmin && (
                    <td className="py-2">
                      <button
                        className="rounded bg-red-700 text-white px-6 py-2 font-semibold hover:bg-red-800"
                        onClick={() => handleDelete(myStudent.id)}
                        disabled={deleting}
                      >
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />
    </>
  );
};

export default ArchivedStudentList;
